import { getCurrentUser as amplifyGetCurrentUser, signOut } from 'aws-amplify/auth';
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ApiSystem, User } from "../state/types";
import { normalizeSystemsData } from '../components/system/systemHelpers';
import { userHasAccess } from '../services/RoleBasedAccess';
import { useSystemsApi } from '../api/useSystemsApi';
import { useAccountApi } from '../api/useAccountApi';
import { stripUnitSuffix } from '../utils/systemCode';
import {
  RELEASE_ID,
  RELEASE_LOGOUT_MESSAGE_KEY,
  RELEASE_STORAGE_KEY,
  RELEASE_VERSION,
  RELEASED_AT,
  formatReleaseDate,
} from '../config/release';

const PUBLIC_PATHS = ['/login', '/logout', '/resetPassword'];

type InitializeAppOptions = {
  forceRefetch?: boolean;
  persistReleaseIdOnSuccess?: boolean;
  skipReleaseValidation?: boolean;
};

export const useSessionState = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [originalUser, setOriginalUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('originalUser');
    return saved ? JSON.parse(saved) : null;
  });
  const [systems, setSystems] = useState<ApiSystem[]>(() => {
    const saved = localStorage.getItem('systems');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedSystem, setSelectedSystem] = useState<ApiSystem | null>(null);
  const [loadingCount, setLoadingCount] = useState(0);
  const isLoading = loadingCount > 0;
  const setIsLoading = useCallback((loading: boolean) => {
    setLoadingCount((current) => {
      if (loading) return current + 1;
      return Math.max(0, current - 1);
    });
  }, []);

  const { getSystemsApi } = useSystemsApi();
  const { loginUserApi } = useAccountApi();
  const selectedSystemRef = useRef<ApiSystem | null>(null);
  const userRef = useRef<User | null>(user);
  const systemsRef = useRef<ApiSystem[]>(systems);

  const handleLogoutCleanup = useCallback(() => {
    setUser(null);
    setOriginalUser(null);
    setSystems([]);
    setSelectedSystem(null);
    localStorage.removeItem('user');
    localStorage.removeItem('systems');
    localStorage.removeItem('originalUser');
    localStorage.removeItem('impersonatedUser');
    localStorage.removeItem(RELEASE_STORAGE_KEY);
  }, []);

  const persistReleaseId = useCallback(() => {
    if (!RELEASE_ID) return;
    localStorage.setItem(RELEASE_STORAGE_KEY, RELEASE_ID);
  }, []);

  const logoutForReleaseChange = useCallback(async () => {
    const releaseDetails = RELEASED_AT
      ? `version ${RELEASE_VERSION}, released ${formatReleaseDate(RELEASED_AT)}`
      : `version ${RELEASE_VERSION}`;

    sessionStorage.setItem(
      RELEASE_LOGOUT_MESSAGE_KEY,
      `The app was updated to ${releaseDetails}. Please sign in again to load the latest release.`
    );

    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out after release change: ', error);
    } finally {
      handleLogoutCleanup();
      navigate('/login', { replace: true });
    }
  }, [handleLogoutCleanup, navigate]);

  const initializeApp = useCallback(async ({
    forceRefetch = false,
    persistReleaseIdOnSuccess = false,
    skipReleaseValidation = false,
  }: InitializeAppOptions = {}) => {
    const isIframePath = location.pathname.toLowerCase().includes('/iframe');

    setIsLoading(true);
    const hasStaleUser = !!localStorage.getItem('user');
    const isMasquerading = !!localStorage.getItem('originalUser');

    try {
      if (isIframePath) {
        return;
      }

      let currentUser: User | null = userRef.current;

      // --- IDENTITY PHASE ---
      if (isMasquerading) {
        // Trust storage for Masquerade; skip API to avoid reverting to Admin data
        const storedImpersonated = localStorage.getItem('impersonatedUser');
        if (storedImpersonated) currentUser = JSON.parse(storedImpersonated);
        await amplifyGetCurrentUser(); // Ensure underlying admin session is alive
      } else {
        // Normal flow: only call API if we don't have a user, or in background
        if (!hasStaleUser || forceRefetch) {
          await amplifyGetCurrentUser();
          currentUser = await loginUserApi();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        }
      }

      const storedReleaseId = localStorage.getItem(RELEASE_STORAGE_KEY);
      if (
        currentUser &&
        RELEASE_ID &&
        !skipReleaseValidation &&
        storedReleaseId !== RELEASE_ID
      ) {
        await logoutForReleaseChange();
        return;
      }

      if (currentUser && RELEASE_ID && persistReleaseIdOnSuccess) {
        persistReleaseId();
      }

      // --- SYSTEMS PHASE ---
      if (currentUser) {
        let finalSystems: ApiSystem[] = [];

        // If we already have systems in state AND we aren't switching modes, skip API
        // Otherwise, fetch fresh to ensure we have the full list to filter from
        console.log("getting systems?")
        if (systemsRef.current.length > 0 && !isMasquerading && !forceRefetch) {
          finalSystems = systemsRef.current;
        } else {
          console.log("getting systems!")
          const rawSystems = await getSystemsApi();
          finalSystems = normalizeSystemsData(rawSystems);
        }

        // Apply masquerade filtering if necessary
        if (isMasquerading && !userHasAccess(currentUser.role, ['Internal'])) {
          const accessibleCodes = new Set((currentUser.system_access || []).map((code: string) => stripUnitSuffix(code)));
          finalSystems = finalSystems.filter(s =>
            accessibleCodes.has(s.systemCode)
          );
        }

        setSystems(finalSystems);
        localStorage.setItem('systems', JSON.stringify(finalSystems));

        // --- SELECTION PHASE ---
        const storageKey = `selectedSystem:${currentUser.username}`;
        const savedCode = stripUnitSuffix(localStorage.getItem(storageKey));
        const currentCode = stripUnitSuffix(selectedSystemRef.current?.systemCode || "");

        const next =
          finalSystems.find(s => s.systemCode === currentCode) ||
          finalSystems.find(s => s.systemCode === savedCode) ||
          finalSystems[0] ||
          null;

        setSelectedSystem(next);
        if (next) localStorage.setItem(storageKey, next.systemCode);
      }
    } catch (error) {
      console.error("Critical Boot Error:", error);
      handleLogoutCleanup();
    } finally {
      setIsLoading(false);
    }
  }, [getSystemsApi, handleLogoutCleanup, location.pathname, loginUserApi, logoutForReleaseChange, persistReleaseId, setIsLoading]);

  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  useEffect(() => {
    selectedSystemRef.current = selectedSystem;
  }, [selectedSystem]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    systemsRef.current = systems;
  }, [systems]);

  useEffect(() => {
    if (isLoading) return;

    const isPublicPath = PUBLIC_PATHS.some(path => location.pathname.startsWith(path));
    const isIframePath = location.pathname.toLowerCase().includes('iframe');

    if (!user && !isPublicPath && !isIframePath) {
      navigate('/login');
    }
  }, [user, isLoading, navigate, location.pathname]);

  const logoutUser = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out: ', error);
    } finally {
      handleLogoutCleanup();
      navigate('/login', { replace: true });
    }
  };

  const startMasquerade = (impersonatedUser: User) => {
    if (user && userHasAccess(user?.role, ['Admin', 'Support'])) {
      setOriginalUser(user);
      setUser(impersonatedUser);
      localStorage.setItem('user', JSON.stringify(impersonatedUser));
      localStorage.setItem('originalUser', JSON.stringify(user));
      localStorage.setItem('impersonatedUser', JSON.stringify(impersonatedUser));

      initializeApp({ forceRefetch: true });
    }
  };

  const stopMasquerade = () => {
    setUser(originalUser);
    setOriginalUser(null);
    localStorage.removeItem('originalUser');
    localStorage.removeItem('impersonatedUser');

    initializeApp({ forceRefetch: true });
  };

  const clearAssetNavStateAndNavigate = useCallback((path: string) => {
    console.log("clearing nav state")
    localStorage.removeItem('assetsNavState');
    navigate(path);
  }, [navigate]);

  const selectSystem = useCallback((sys: ApiSystem | null) => {
    setSelectedSystem(sys);
    if (!user) return;
    const key = `selectedSystem:${user.username}`;
    if (sys) localStorage.setItem(key, sys.systemCode);
    else localStorage.removeItem(key);
    if (location.pathname.startsWith('/asset/')) {
      clearAssetNavStateAndNavigate('/assets');
    }
  }, [clearAssetNavStateAndNavigate, location.pathname, user]);

  const loginUser = useCallback(() => initializeApp({
    forceRefetch: true,
    persistReleaseIdOnSuccess: true,
    skipReleaseValidation: true,
  }), [initializeApp]);

  const refetchSystems = useCallback((forceRefetch = false) => {
    return initializeApp({ forceRefetch });
  }, [initializeApp]);

  return {
    // User Session
    user,
    originalUser,
    isLoggedIn: !!user,
    isLoading,
    setIsLoading,
    loginUser,
    logoutUser,
    startMasquerade,
    stopMasquerade,
    handleNavigation: clearAssetNavStateAndNavigate,

    // Systems storage
    systems,
    setSystems,
    selectedSystem,
    selectSystem,
    refetchSystems
  };
};
